import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  WifiIcon, 
  CloudArrowDownIcon,
  ExclamationTriangleIcon 
} from '@heroicons/react/24/outline';
import { usePWA } from '../../contexts/PWAContext';
import { Badge } from '../ui';

interface OfflineIndicatorProps {
  variant?: 'badge' | 'banner' | 'subtle';
  position?: 'top' | 'bottom';
}

const OfflineIndicator: React.FC<OfflineIndicatorProps> = ({ 
  variant = 'badge',
  position = 'top'
}) => {
  const { isOffline } = usePWA();

  if (variant === 'badge') {
    return (
      <AnimatePresence>
        {isOffline && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
          >
            <Badge 
              variant="warning" 
              className="flex items-center space-x-1"
            >
              <WifiIcon className="w-3 h-3" />
              <span>Offline</span>
            </Badge>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  if (variant === 'banner') {
    return (
      <AnimatePresence>
        {isOffline && (
          <motion.div
            initial={{ y: position === 'top' ? -100 : 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: position === 'top' ? -100 : 100, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className={`
              fixed left-0 right-0 z-40 bg-yellow-50 dark:bg-yellow-900/20 
              border-yellow-200 dark:border-yellow-800 border-b
              ${position === 'top' ? 'top-0' : 'bottom-0'}
            `}
          >
            <div className="px-4 py-3">
              <div className="flex items-center justify-center space-x-2">
                <WifiIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-500" />
                <span className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                  You're offline. Some features may be limited.
                </span>
                <CloudArrowDownIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-500" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  // Subtle variant
  return (
    <AnimatePresence>
      {isOffline && (
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2 }}
          className="flex items-center space-x-2 text-yellow-600 dark:text-yellow-400"
        >
          <ExclamationTriangleIcon className="w-4 h-4" />
          <span className="text-sm">Offline mode</span>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default OfflineIndicator;
